import { Copy } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { describeBundleRelevanceStatus } from "@/relevance-review/bundleRelevanceStatusPresentation";
import type { BundleRelevanceStatus } from "@/relevance-review/useBundleRelevanceStatusesForEncounter";
import { fileCount, fileLabel, identificationText } from "./bundleText";
import { formatBundleDateTime } from "./formatBundleDateTime";
import type { RawFilesBundleRow } from "./useBundlesForPatient";

interface BundleAttributesCardProps {
  bundle: RawFilesBundleRow;
  relevanceStatus: BundleRelevanceStatus | null;
  /** Page numbers the summary did not read, by file key. */
  skippedPagesByFile?: ReadonlyMap<string, readonly number[]>;
}

function skippedPagesText(skippedPagesByFile: ReadonlyMap<string, readonly number[]> | undefined): string | null {
  if (!skippedPagesByFile || skippedPagesByFile.size === 0) return null;

  const entries = [...skippedPagesByFile];
  if (entries.length === 1) return entries[0][1].join(", ");

  return entries.map(([key, pages]) => `${fileLabel(key)}: ${pages.join(", ")}`).join("; ");
}

function fileSummary(files: RawFilesBundleRow["files"]) {
  const countsByType = new Map<string, number>();
  for (const file of files) countsByType.set(file.type, (countsByType.get(file.type) ?? 0) + 1);
  const types = [...countsByType].map(([type, count]) => `${count} ${type}`).join(", ");
  return `${fileCount(files.length)} (${types})`;
}

async function copyBundleId(bundleId: string) {
  try {
    await navigator.clipboard.writeText(bundleId);
    toast.success("Bundle id copied");
  } catch {
    toast.error("Couldn't copy to clipboard.");
  }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined) return null;

  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  );
}

export function BundleAttributesCard({ bundle, relevanceStatus, skippedPagesByFile }: BundleAttributesCardProps) {
  const { identification } = bundle;

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      <Row label="Source">{bundle.source}</Row>
      <Row label="Bundle type">{bundle.bundle_type}</Row>
      <Row label="Document date">{bundle.bundle_date ? formatBundleDateTime(bundle.bundle_date) : null}</Row>
      <Row label="Ingested">{formatBundleDateTime(bundle.created_at)}</Row>
      <Row label="Identified">
        {identification
          ? `${identificationText(identification)}, ${formatBundleDateTime(identification.identified_at)}`
          : null}
      </Row>
      <Row label="Relevance">{relevanceStatus ? describeBundleRelevanceStatus(relevanceStatus).label : null}</Row>
      <Row label="Pages not read by the summary">{skippedPagesText(skippedPagesByFile)}</Row>
      <Row label="Files">{bundle.files.length > 0 ? fileSummary(bundle.files) : null}</Row>
      <Row label="CSN">{bundle.csn}</Row>
      <Row label="Revision">{bundle.bundle_revision}</Row>
      <dt className="text-muted-foreground">Bundle id</dt>
      <dd className="flex items-start gap-1">
        <span className="break-all font-mono">{bundle.id}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          aria-label="Copy bundle id"
          onClick={() => copyBundleId(bundle.id)}
        >
          <Copy className="h-3 w-3" aria-hidden="true" />
        </Button>
      </dd>
    </dl>
  );
}
